/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Utilities for chapter content tree rendering and selection.
 *
 * @file chapterContentTreeUtils.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import Utils from '../../common/utils';
import { SYLLABUS_UI_CONSTANTS, SCHEDULE_MENU_HIDDEN_ITEM_IDS } from '../../common/constants';

export function joinClasses(...parts) {
  return parts.filter(Boolean).join(' ');
}

export function getScrollContainerStyle(style = {}) {
  const scrollStyle = {
    minHeight: style.minHeight ?? SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MIN_HEIGHT,
    maxHeight: style.maxHeight ?? SYLLABUS_UI_CONSTANTS.CHECKBOX_CONTAINER_MAX_HEIGHT
  };

  if (style.height !== undefined) {
    scrollStyle.height = style.height;
  }

  if (style.flex !== undefined) {
    scrollStyle.flex = style.flex;
  }

  return scrollStyle;
}

export function getScheduleMenuHiddenItemIds(
  textInfo = {},
  {
    isChecked = false,
    isIndeterminate = false,
    selectedQuestionCount = 0,
    nodeQuestionCount,
    isAllDescendantsAssigned = false
  } = {}
) {
  const config = textInfo.scheduleMenuConfig || {};
  const hiddenAlways = config.hiddenAlways || [];
  const hiddenWhenUnchecked = config.hiddenWhenUnchecked || [];
  const hidden = [...hiddenAlways];
  const effectiveQuestionCount = nodeQuestionCount ?? selectedQuestionCount;

  if (isAllDescendantsAssigned) {
    hidden.push(SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE);
  }

  if (effectiveQuestionCount <= 0) {
    hidden.push(
      SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE,
      SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS
    );
  }

  hiddenWhenUnchecked.forEach((menuItemId) => {
    if (menuItemId === SCHEDULE_MENU_HIDDEN_ITEM_IDS.QUESTIONS) {
      const showQuestions = (isChecked || isIndeterminate) && selectedQuestionCount > 0;

      if (!showQuestions) {
        hidden.push(menuItemId);
      }

      return;
    }

    if (!isChecked) {
      hidden.push(menuItemId);
    }
  });

  return [...new Set(hidden)];
}

export function buildScheduleMenuItems(
  node,
  onAddToDate,
  onScheduleMenuAction,
  { textInfo = {}, hiddenMenuItemIds = [] } = {}
) {
  const hiddenIds = new Set(hiddenMenuItemIds);
  const config = textInfo.scheduleMenuConfig || {};
  const menuItems = config.items || [];
  const labels = config.labels || {};

  const handleMenuItemClick = (menuItemId) => {
    if (menuItemId === SCHEDULE_MENU_HIDDEN_ITEM_IDS.ADD_TO_DATE) {
      onAddToDate?.(node);

      return;
    }

    onScheduleMenuAction?.(menuItemId, node);
  };

  const visibleMenuItems = menuItems
    .filter((menuItem) => !hiddenIds.has(menuItem.id))
    .map((menuItem) => ({
      ...menuItem,
      label: labels[menuItem.id] || menuItem.label,
      onClick: () => handleMenuItemClick(menuItem.id)
    }));

  return visibleMenuItems.map((menuItem, index) => ({
    ...menuItem,
    divider: index === 0 ? false : menuItem.divider
  }));
}

export function isNodeSelected(node, selectedNodes) {
  const nodeId = node.id;

  return selectedNodes.some((selected) => {
    if (typeof selected === 'object') {
      return selected.id === nodeId;
    }

    return selected === nodeId;
  });
}

export function areAllDescendantsSelected(node, selectedNodes) {
  if (!node.children || node.children.length === 0) {
    return isNodeSelected(node, selectedNodes);
  }

  const isChildFullySelected = (child) => {
    if (!child.children || child.children.length === 0) {
      return isNodeSelected(child, selectedNodes);
    }

    return areAllDescendantsSelected(child, selectedNodes);
  };

  return node.children.every((child) => isChildFullySelected(child));
}

export function areAllUnassignedDescendantsSelected(node, selectedNodes) {
  if (!node.children || node.children.length === 0) {
    if (node.isSectionAssigned) {
      return true;
    }

    return isNodeSelected(node, selectedNodes);
  }

  return node.children.every((child) => {
    if (child.isSectionAssigned) {
      return true;
    }

    if (child.children && child.children.length > 0) {
      return areAllUnassignedDescendantsSelected(child, selectedNodes);
    }

    return isNodeSelected(child, selectedNodes);
  });
}

export function areSomeDescendantsSelected(node, selectedNodes) {
  if (!node.children || node.children.length === 0) {
    return false;
  }

  const hasAnySelected = (n) => {
    if (n.isSectionAssigned) {
      if (n.children && n.children.length > 0) {
        return n.children.some((child) => hasAnySelected(child));
      }

      return false;
    }

    if (isNodeSelected(n, selectedNodes)) {
      return true;
    }

    if (n.children && n.children.length > 0) {
      return n.children.some((child) => hasAnySelected(child));
    }

    return false;
  };

  const someSelected = node.children.some((child) => hasAnySelected(child));

  if (!someSelected) {
    return false;
  }

  return !areAllDescendantsSelected(node, selectedNodes);
}

export function toggleNodeInSelection(node, selectedNodes) {
  const chapterId = typeof node === 'object' ? node.id : node;

  const alreadySelected = selectedNodes.some((selected) => {
    if (typeof selected === 'object') {
      return selected.id === chapterId;
    }

    return selected === chapterId;
  });

  if (alreadySelected) {
    return selectedNodes.filter((selected) => {
      if (typeof selected === 'object') {
        return selected.id !== chapterId;
      }

      return selected !== chapterId;
    });
  }

  return [...selectedNodes, node];
}

function applyDescendantSelection(node, shouldSelect, selectedNodes) {
  let next = selectedNodes;

  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      const isChildSelected = isNodeSelected(child, next);

      if (shouldSelect !== isChildSelected && (!shouldSelect || !child.isSectionAssigned)) {
        next = toggleNodeInSelection(child, next);
      }

      next = applyDescendantSelection(child, shouldSelect, next);
    });
  }

  return next;
}

export function computeNextSelection(node, selectedNodes) {
  const hasChildren = node.children && node.children.length > 0;
  const allDescendantsSelected = areAllDescendantsSelected(node, selectedNodes);
  const allUnassignedDescendantsSelected = areAllUnassignedDescendantsSelected(node, selectedNodes);

  if (hasChildren) {
    if (allDescendantsSelected || allUnassignedDescendantsSelected) {
      let next = applyDescendantSelection(node, false, selectedNodes);

      if (isNodeSelected(node, next)) {
        next = toggleNodeInSelection(node, next);
      }

      return next;
    }

    let next = selectedNodes;

    if (!isNodeSelected(node, selectedNodes)) {
      next = toggleNodeInSelection(node, next);
    }

    return applyDescendantSelection(node, true, next);
  }

  return toggleNodeInSelection(node, selectedNodes);
}

export function selectAllDescendants(node, shouldSelect, selectedNodes, onToggleNode) {
  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      const isChildSelected = isNodeSelected(child, selectedNodes);

      if (shouldSelect !== isChildSelected && (!shouldSelect || !child.isSectionAssigned)) {
        onToggleNode(child);
      }

      selectAllDescendants(child, shouldSelect, selectedNodes, onToggleNode);
    });
  }
}

export function toggleNodeSelection(node, selectedNodes, onToggleNode) {
  const hasChildren = node.children && node.children.length > 0;
  const allDescendantsSelected = areAllDescendantsSelected(node, selectedNodes);
  const allUnassignedDescendantsSelected = areAllUnassignedDescendantsSelected(node, selectedNodes);

  if (hasChildren) {
    if (allDescendantsSelected || allUnassignedDescendantsSelected) {
      selectAllDescendants(node, false, selectedNodes, onToggleNode);

      if (isNodeSelected(node, selectedNodes)) {
        onToggleNode(node);
      }
    } else {
      const isNodeCurrentlySelected = isNodeSelected(node, selectedNodes);

      if (!isNodeCurrentlySelected) {
        onToggleNode(node);
      }

      selectAllDescendants(node, true, selectedNodes, onToggleNode);
    }
  } else {
    onToggleNode(node);
  }
}

export function findTopLevelChapterId(targetId, nodes, parentChapterId = null) {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    const currentParentId = node.type === 'chapter' ? node.id : parentChapterId;

    if (node.id === targetId) {
      return currentParentId || parentChapterId;
    }

    if (node.children && node.children.length > 0) {
      const found = findTopLevelChapterId(targetId, node.children, currentParentId);

      if (found) {
        return found;
      }
    }
  }

  return null;
}

export function calculateQuestionsForNode(node, questionCounts, nodes) {
  let totalQuestions = 0;

  if (!node.children || node.children.length === 0) {
    if (!Array.isArray(questionCounts)) {
      return 0;
    }

    const parentChapterId = findTopLevelChapterId(node.id, nodes);

    if (parentChapterId) {
      const chapterData = questionCounts.find((ch) => ch.chapterId === parentChapterId);

      if (chapterData?.questions) {
        const nodeQuestions = chapterData.questions.filter((q) => q.pageId === node.id);

        return nodeQuestions.length;
      }
    }

    return 0;
  }

  if (node.children) {
    node.children.forEach((child) => {
      totalQuestions += calculateQuestionsForNode(child, questionCounts, nodes);
    });
  }

  return totalQuestions;
}

export function calculateQuestionsForSelectedNode(node, selectedNodes, questionCounts, nodes) {
  if (!Array.isArray(questionCounts) || questionCounts.length === 0) {
    return 0;
  }

  const hasChildren = node.children && node.children.length > 0;

  if (!hasChildren) {
    return isNodeSelected(node, selectedNodes)
      ? calculateQuestionsForNode(node, questionCounts, nodes)
      : 0;
  }

  return node.children.reduce(
    (sum, child) => sum + calculateQuestionsForSelectedNode(child, selectedNodes, questionCounts, nodes),
    0
  );
}

export function getQuestionCountText(node, questionCounts, nodes, textInfo) {
  const totalQuestions = calculateQuestionsForNode(node, questionCounts, nodes);

  return `${totalQuestions} ${textInfo.questionsLabel}`;
}

export function getTooltipText(
  isParentNode,
  isAllDescendantsAssigned,
  hasSomeAssignedDescendants,
  textInfo
) {
  if (isParentNode && isAllDescendantsAssigned) {
    return textInfo.includedInAnotherAssignmentTooltip;
  }

  if (isParentNode && hasSomeAssignedDescendants) {
    return textInfo.someSectionsIncludedInAnotherAssignmentTooltip;
  }

  if (isAllDescendantsAssigned) {
    return textInfo.includedInAnotherAssignmentTooltip;
  }

  return null;
}

export function getNodeDisabledState(node, selectedNodes, restrictAssigned) {
  const hasChildren = node.children && node.children.length > 0;
  const isParentNode = hasChildren;
  const allDescendantsSelected = areAllDescendantsSelected(node, selectedNodes);
  const isChecked = allDescendantsSelected;
  const isAllDescendantsAssigned = restrictAssigned && Utils.isAllDescendantsAssigned(node);

  return (
    restrictAssigned
    && (isParentNode
      ? isAllDescendantsAssigned
      : Utils.isNodeOrDescendantAssigned(node) && !isChecked)
  );
}

export function getAssignedStatusLabel(node, restrictAssigned, textInfo) {
  if (!restrictAssigned) {
    return null;
  }

  const isAllDescendantsAssigned = Utils.isAllDescendantsAssigned(node);
  const hasSomeAssignedDescendants = Utils.hasSomeAssignedDescendants(node);

  if (isAllDescendantsAssigned) {
    return textInfo.alreadyAssignedLabel || 'Already assigned';
  }

  if (hasSomeAssignedDescendants) {
    return textInfo.partiallyAssignedLabel || 'Partially assigned';
  }

  return null;
}

export function expandAncestorsOfSelected(nodes, selectedNodes, setExpandedNodes) {
  if (!selectedNodes || selectedNodes.length === 0) {
    return;
  }

  const selectedIds = new Set(selectedNodes.map((item) => (
    typeof item === 'object' ? item.id : item
  )));

  const expandRecursively = (treeNodes, path = []) => {
    for (let i = 0; i < treeNodes.length; i += 1) {
      const node = treeNodes[i];
      const isSelected = selectedIds.has(node.id);
      const newPath = [...path, node.id];

      if (isSelected) {
        path.forEach((ancestorId) => {
          setExpandedNodes((prev) => ({
            ...prev,
            [ancestorId]: true
          }));
        });
      }

      if (node.children && node.children.length > 0) {
        expandRecursively(node.children, newPath);
      }
    }
  };

  expandRecursively(nodes);
}

export function buildDragPayload(node) {
  return JSON.stringify({
    id: node.id,
    title: node.title,
    type: node.type
  });
}

export function parseDragPayload(rawPayload) {
  if (!rawPayload) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawPayload);

    if (!parsed?.id) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function findNodeById(nodes = [], targetId) {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];

    if (node.id === targetId) {
      return node;
    }

    if (node.children?.length) {
      const found = findNodeById(node.children, targetId);

      if (found) {
        return found;
      }
    }
  }

  return null;
}

export function collectAssignedContentNodeIds(assignments = []) {
  const assignedIds = new Set();

  assignments.forEach((assignment) => {
    (assignment.contentNodes || []).forEach((node) => {
      if (node?.id) {
        assignedIds.add(node.id);
      }
    });
  });

  return assignedIds;
}

/** Expand parent selections so chapter-level drops mark all descendants as assigned. */
export function expandAssignedNodeIdsForRestriction(nodeIds = [], tocData = []) {
  const expanded = new Set();

  const addNodeAndDescendants = (node) => {
    if (!node?.id) {
      return;
    }

    expanded.add(node.id);
    (node.children || []).forEach(addNodeAndDescendants);
  };

  nodeIds.forEach((id) => {
    const node = findNodeById(tocData, id);

    if (node?.children?.length) {
      addNodeAndDescendants(node);
    } else if (node?.id) {
      expanded.add(node.id);
    } else {
      expanded.add(id);
    }
  });

  return [...expanded];
}

/** Mark assigned sections in the TOC without removing nodes (add-manually restrict pattern). */
export function buildScheduleRestrictedTocData(sourceTocData = [], assignments = []) {
  if (!sourceTocData.length) {
    return sourceTocData;
  }

  const assignedSectionIds = expandAssignedNodeIdsForRestriction(
    [...collectAssignedContentNodeIds(assignments)],
    sourceTocData
  );

  return Utils.markAssignedSections(sourceTocData, assignedSectionIds);
}

/** Return a copy of a tree node with already-assigned descendants removed. */
export function getUnassignedNodeForSchedule(node, sourceTocData = [], assignments = []) {
  if (!node?.id) {
    return null;
  }

  const sourceNode = findNodeById(sourceTocData, node.id) || node;
  const assignedIds = new Set(
    expandAssignedNodeIdsForRestriction(
      [...collectAssignedContentNodeIds(assignments)],
      sourceTocData
    )
  );
  const clonedNode = JSON.parse(JSON.stringify(sourceNode));

  if (!assignedIds.size) {
    return clonedNode;
  }

  // eslint-disable-next-line no-use-before-define
  const filteredNodes = filterTreeByAssignedIds([clonedNode], assignedIds);

  return filteredNodes[0] || null;
}

export function filterTreeByAssignedIds(nodes = [], assignedIds = new Set()) {
  if (!assignedIds.size) {
    return nodes;
  }

  return nodes.reduce((accumulator, node) => {
    if (assignedIds.has(node.id)) {
      return accumulator;
    }

    const children = node.children || [];

    if (!children.length) {
      accumulator.push(node);

      return accumulator;
    }

    const filteredChildren = filterTreeByAssignedIds(children, assignedIds);

    if (!filteredChildren.length) {
      return accumulator;
    }

    accumulator.push({
      ...node,
      children: filteredChildren
    });

    return accumulator;
  }, []);
}

export function getChapterRowDisplayState({
  node,
  level,
  selectedNodes,
  restrictAssigned,
  variant,
  expandedNodes,
  textInfo,
  questionCounts,
  nodes,
  classes
}) {
  const nodeId = node.id;
  const children = node.children || [];
  const isExpanded = expandedNodes[nodeId];
  const hasChildren = children.length > 0;
  const isTopLevel = level === 0;
  const isPickerVariant = variant === 'picker';
  const isScheduleVariant = variant === 'schedule';

  const isChecked = hasChildren && restrictAssigned
    ? areAllUnassignedDescendantsSelected(node, selectedNodes)
    : areAllDescendantsSelected(node, selectedNodes);
  const isSelectionIndeterminate = hasChildren
    && !isChecked
    && areSomeDescendantsSelected(node, selectedNodes);
  const isIndeterminate = isSelectionIndeterminate;
  const isDisabled = getNodeDisabledState(node, selectedNodes, restrictAssigned);
  const isAllDescendantsAssigned = restrictAssigned && Utils.isAllDescendantsAssigned(node);
  const hasSomeAssignedDescendants = restrictAssigned && Utils.hasSomeAssignedDescendants(node);
  const tooltipText = getTooltipText(
    hasChildren,
    isAllDescendantsAssigned,
    hasSomeAssignedDescendants,
    textInfo
  );
  const statusLabel = getAssignedStatusLabel(node, restrictAssigned, textInfo);
  const showAssignedStatus = Boolean(statusLabel) && isTopLevel;
  const showQuestionCount = !showAssignedStatus && questionCounts && nodes;
  const isAssignedNode = restrictAssigned && Boolean(node.isSectionAssigned);
  const isTopLevelDragDisabled = isScheduleVariant && isTopLevel && Boolean(statusLabel);
  const showDragHandle = isScheduleVariant;
  const isDraggable = !isTopLevelDragDisabled
    && !(isScheduleVariant && restrictAssigned && isAssignedNode);

  return {
    nodeId,
    children,
    isExpanded,
    hasChildren,
    isTopLevel,
    isPickerVariant,
    isScheduleVariant,
    isChecked,
    isIndeterminate,
    isDisabled,
    isAllDescendantsAssigned,
    tooltipText,
    statusLabel,
    showAssignedStatus,
    showQuestionCount,
    wrapperClassName: isTopLevel ? classes.node : classes.nestedNode,
    rowContentClassName: joinClasses(classes.rowContent, !isTopLevel && classes.rowContentModule),
    labelClassName: joinClasses(
      classes.label,
      isTopLevel ? classes.labelTopLevel : classes.labelModule,
      isDisabled && classes.labelDisabled
    ),
    rowDepthStyle: { '--depth': level },
    showDragHandle,
    isDraggable: isScheduleVariant && isDraggable
  };
}
